/**
 * Whitelisted arithmetic for `countdown`: a recursive-descent parser and an exact evaluator.
 * TypeScript port of `backend/apps/puzzletypes/countdown/arithmetic.py`; parity is held by
 * `shared/fixtures/countdown`. Never `eval()`, never `Function()`:
 *
 *     expr   := term (("+" | "-") term)*
 *     term   := factor (("*" | "/") factor)*
 *     factor := NUMBER | "(" expr ")"
 *
 * No unary minus, no decimal point, no implicit multiplication. Values are exact rationals on
 * `bigint`, so a product of eight numbers never loses a digit. Positions count Unicode code
 * points, as Python does.
 */

export type Op = "+" | "-" | "*" | "/";

/** Every character an operator may be typed as, folded to its ASCII form. */
export const OPERATORS: Readonly<Record<string, Op>> = {
  "+": "+",
  "-": "-",
  "−": "-", // − minus sign
  "–": "-", // – en dash, which phone keyboards substitute
  "*": "*",
  "×": "*", // ×
  x: "*",
  X: "*",
  "/": "/",
  "÷": "/", // ÷
  ":": "/", // the French school division sign
};
const SPACES = new Set([" ", "\t", "\n", "\r", " ", " "]);
const DIGITS = new Set("0123456789");
/** Canonical display of each operator, used by the Player's buttons. */
export const DISPLAY: Readonly<Record<Op, string>> = {
  "+": "+",
  "-": "−",
  "*": "×",
  "/": "÷",
};

export interface Token {
  kind: "num" | "op" | "(" | ")";
  text: string;
  position: number;
}

export type Node =
  | { kind: "num"; value: bigint; position: number }
  | { kind: "op"; op: Op; left: Node; right: Node };

export interface Issue {
  code: string;
  params: Record<string, unknown>;
}

export class ExpressionError extends Error {
  readonly issue: Issue;
  constructor(code: string, params: Record<string, unknown> = {}) {
    super(code);
    this.issue = { code, params };
  }
}

// ---- exact rationals ----------------------------------------------------------------

/** A reduced fraction; the denominator is always positive. */
export interface Rational {
  n: bigint;
  d: bigint;
}

function abs(x: bigint): bigint {
  return x < 0n ? -x : x;
}

function gcd(a: bigint, b: bigint): bigint {
  let x = abs(a);
  let y = abs(b);
  while (y !== 0n) [x, y] = [y, x % y];
  return x;
}

export function rational(n: bigint, d: bigint = 1n): Rational {
  if (d === 0n) throw new RangeError("zero denominator");
  const sign = d < 0n ? -1n : 1n;
  const g = gcd(n, d) || 1n;
  return { n: (sign * n) / g, d: (sign * d) / g };
}

function combine(op: Op, a: Rational, b: Rational): Rational {
  switch (op) {
    case "+":
      return rational(a.n * b.d + b.n * a.d, a.d * b.d);
    case "-":
      return rational(a.n * b.d - b.n * a.d, a.d * b.d);
    case "*":
      return rational(a.n * b.n, a.d * b.d);
    case "/":
      return rational(a.n * b.d, a.d * b.n);
  }
}

/** `3`, `7/2`, `-1/3`: the same spelling as Python's `str(Fraction)`. */
export function formatValue(value: Rational): string {
  return value.d === 1n ? value.n.toString() : `${value.n}/${value.d}`;
}

export function equalsInteger(value: Rational, target: number): boolean {
  return value.d === 1n && value.n === BigInt(target);
}

// ---- parsing ------------------------------------------------------------------------

export function tokenize(expression: string): Token[] {
  const chars = Array.from(expression);
  const tokens: Token[] = [];
  let i = 0;
  while (i < chars.length) {
    const char = chars[i];
    if (SPACES.has(char)) {
      i += 1;
    } else if (DIGITS.has(char)) {
      const start = i;
      while (i < chars.length && DIGITS.has(chars[i])) i += 1;
      tokens.push({ kind: "num", text: chars.slice(start, i).join(""), position: start });
    } else if (Object.hasOwn(OPERATORS, char)) {
      tokens.push({ kind: "op", text: OPERATORS[char], position: i });
      i += 1;
    } else if (char === "(" || char === ")") {
      tokens.push({ kind: char, text: char, position: i });
      i += 1;
    } else {
      throw new ExpressionError("expression_syntax", { column: i + 1 });
    }
  }
  return tokens;
}

/** Length in code points: where an unexpected end is reported. */
export function lengthOf(expression: string): number {
  return Array.from(expression).length;
}

class Parser {
  private index = 0;
  constructor(
    private readonly tokens: Token[],
    private readonly end: number,
  ) {}

  peek(): Token | undefined {
    return this.tokens[this.index];
  }

  fail(): ExpressionError {
    const token = this.peek();
    // 1-based, as a person counts: the first character is column 1.
    return new ExpressionError("expression_syntax", {
      column: (token ? token.position : this.end) + 1,
    });
  }

  expr(): Node {
    let node = this.term();
    for (let token = this.peek(); token?.kind === "op" && "+-".includes(token.text);) {
      this.index += 1;
      node = { kind: "op", op: token.text as Op, left: node, right: this.term() };
      token = this.peek();
    }
    return node;
  }

  term(): Node {
    let node = this.factor();
    for (let token = this.peek(); token?.kind === "op" && "*/".includes(token.text);) {
      this.index += 1;
      node = { kind: "op", op: token.text as Op, left: node, right: this.factor() };
      token = this.peek();
    }
    return node;
  }

  factor(): Node {
    const token = this.peek();
    if (!token) throw this.fail();
    if (token.kind === "num") {
      this.index += 1;
      return { kind: "num", value: BigInt(token.text), position: token.position };
    }
    if (token.kind === "(") {
      this.index += 1;
      const node = this.expr();
      if (this.peek()?.kind !== ")") throw this.fail();
      this.index += 1;
      return node;
    }
    throw this.fail();
  }
}

/** The expression's tree, or `ExpressionError` (`expression_empty` / `expression_syntax`). */
export function parse(expression: string): Node {
  const tokens = tokenize(expression);
  if (tokens.length === 0) throw new ExpressionError("expression_empty");
  const parser = new Parser(tokens, lengthOf(expression));
  const node = parser.expr();
  if (parser.peek()) throw parser.fail();
  return node;
}

/** The numbers of the expression, left to right. */
export function literals(node: Node): bigint[] {
  return node.kind === "num" ? [node.value] : [...literals(node.left), ...literals(node.right)];
}

/**
 * Exact value, operand by operand (left subtree, right subtree, then the operation). Without
 * `allowFractions` every intermediate result must be a positive integer; the first one that is
 * not is reported with its value. Dividing by zero is never allowed.
 */
export function evaluate(node: Node, allowFractions: boolean): Rational {
  if (node.kind === "num") return rational(node.value);
  const left = evaluate(node.left, allowFractions);
  const right = evaluate(node.right, allowFractions);
  if (node.op === "/" && right.n === 0n) throw new ExpressionError("division_by_zero");
  const value = combine(node.op, left, right);
  if (!allowFractions) {
    if (value.d !== 1n) throw new ExpressionError("fraction_result", { value: formatValue(value) });
    if (value.n <= 0n)
      throw new ExpressionError("non_positive_result", { value: formatValue(value) });
  }
  return value;
}

function counter(values: Iterable<bigint>): Map<bigint, number> {
  const counts = new Map<bigint, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return counts;
}

/** Numbers that are not given, or used more often than given: one issue per value. */
export function numberIssues(numbers: readonly number[], used: readonly bigint[]): Issue[] {
  const available = counter(numbers.map((n) => BigInt(n)));
  const seen = new Map<bigint, number>();
  const reported = new Set<bigint>();
  const issues: Issue[] = [];
  for (const value of used) {
    if (reported.has(value)) continue;
    const given = available.get(value);
    if (given === undefined) {
      issues.push({ code: "unknown_number", params: { number: value.toString() } });
      reported.add(value);
      continue;
    }
    const count = (seen.get(value) ?? 0) + 1;
    seen.set(value, count);
    if (count > given) {
      issues.push({ code: "number_reused", params: { number: value.toString() } });
      reported.add(value);
    }
  }
  return issues;
}

/** The given numbers the expression leaves out, in the order they are given. */
export function unusedNumbers(numbers: readonly number[], used: readonly bigint[]): number[] {
  const remaining = counter(used);
  const unused: number[] = [];
  for (const number of numbers) {
    const key = BigInt(number);
    const left = remaining.get(key) ?? 0;
    if (left > 0) remaining.set(key, left - 1);
    else unused.push(number);
  }
  return unused;
}

export interface Rules {
  numbers: readonly number[];
  target: number;
  useAll?: boolean;
  allowFractions?: boolean;
}

export interface Reading {
  issues: Issue[];
  value: Rational | null;
}

/** Judges an expression against the public rules. No issue means it is an answer. */
export function read(expression: string, rules: Rules): Reading {
  let tree: Node;
  try {
    tree = parse(expression);
  } catch (error) {
    if (error instanceof ExpressionError) return { issues: [error.issue], value: null };
    throw error;
  }
  const used = literals(tree);
  const issues = numberIssues(rules.numbers, used);
  if (issues.length > 0) return { issues, value: null };
  let value: Rational;
  try {
    value = evaluate(tree, rules.allowFractions ?? false);
  } catch (error) {
    if (error instanceof ExpressionError) return { issues: [error.issue], value: null };
    throw error;
  }
  if (!equalsInteger(value, rules.target))
    issues.push({ code: "wrong_result", params: { value: formatValue(value) } });
  if (rules.useAll) {
    const unused = unusedNumbers(rules.numbers, used);
    if (unused.length > 0)
      issues.push({
        code: "numbers_unused",
        params: { numbers: unused.join(", "), count: unused.length },
      });
  }
  return { issues, value };
}

/** Codes that say the expression breaks a rule, as opposed to not being finished yet. */
const FINAL = new Set(["wrong_result", "numbers_unused", "expression_empty"]);

export interface LiveReading {
  /** Broken rules only: never "wrong total" or "numbers left", which typing may still fix. */
  issues: Issue[];
  /** The value of the player's own expression, when it reads as a whole and breaks nothing. */
  value: Rational | null;
}

/**
 * What the Player may say while the expression is being typed. An expression that is only
 * unfinished (it breaks at its very end) is not an error: its numbers are checked, nothing
 * else. When in doubt, silence (CLAUDE.md §16.4).
 */
export function readLive(expression: string, rules: Rules): LiveReading {
  let tokens: Token[];
  try {
    tokens = tokenize(expression);
  } catch (error) {
    if (error instanceof ExpressionError) return { issues: [error.issue], value: null };
    throw error;
  }
  try {
    parse(expression);
  } catch (error) {
    if (!(error instanceof ExpressionError)) throw error;
    const { code, params } = error.issue;
    if (code === "expression_syntax" && params.column !== lengthOf(expression) + 1)
      return { issues: [error.issue], value: null };
    // Unfinished: the numbers typed so far can still be judged.
    const used = tokens.filter((t) => t.kind === "num").map((t) => BigInt(t.text));
    return { issues: numberIssues(rules.numbers, used), value: null };
  }
  const reading = read(expression, rules);
  return { issues: reading.issues.filter((i) => !FINAL.has(i.code)), value: reading.value };
}
